import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function AdminMfaReset() {
  const navigate = useNavigate();
  const [token] = useState(() => {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const value = params.get("token") || "";

    if (window.location.hash) {
      window.history.replaceState(
        null,
        document.title,
        `${window.location.pathname}${window.location.search}`
      );
    }

    return value;
  });

  const [account, setAccount] = useState(null);
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Missing MFA reset token.");
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const accountResult = await api.post("/auth/mfa-reset/validate", {
          token,
        });
        setAccount(accountResult.data);

        const mfaResult = await api.post("/auth/mfa-reset/start", {
          token,
        });
        setMfa(mfaResult.data);
      } catch (err) {
        setError(
          err?.response?.data?.detail ||
            "This MFA reset link is invalid or has expired."
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const verify = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");

    try {
      const { data } = await api.post("/auth/mfa-reset/verify", {
        token,
        code,
      });

      setRecoveryCodes(data.recovery_codes || []);
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Unable to verify MFA."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-lightbg px-5 py-10">
      <div className="mx-auto max-w-lg">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>

        <div className="rounded-md border border-brandborder bg-white p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="rounded-sm bg-navy p-2.5 text-cyan">
              <Smartphone size={22} />
            </div>

            <div>
              <h1 className="font-heading text-2xl font-bold text-navy">
                Reset authenticator MFA
              </h1>
              {account?.email && (
                <p className="mt-1 text-sm text-slate">{account.email}</p>
              )}
            </div>
          </div>

          {loading ? (
            <div className="mt-7 text-sm text-slate">Preparing MFA reset...</div>
          ) : error ? (
            <div className="mt-7 rounded-sm border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              {error}
            </div>
          ) : recoveryCodes.length > 0 ? (
            <div className="mt-7">
              <h2 className="font-semibold text-navy">MFA reset complete</h2>

              <p className="mt-2 text-sm text-slate">
                Save these new recovery codes securely. Each code can be used
                once.
              </p>

              <div className="mt-4 grid gap-2 rounded-sm bg-lightbg p-4 font-mono text-sm text-navy">
                {recoveryCodes.map((item) => (
                  <div key={item}>{item}</div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => navigate("/admin/login")}
                className="mt-6 w-full rounded-sm bg-navy px-4 py-3 text-sm font-semibold text-white"
              >
                Continue to sign in
              </button>
            </div>
          ) : (
            mfa && (
              <form onSubmit={verify} className="mt-7">
                <p className="text-sm text-slate">
                  Scan this QR code using the user's new authenticator app.
                </p>

                <div className="mt-5 flex justify-center p-4">
                  <QRCodeSVG value={mfa.provisioning_uri} size={190} />
                </div>

                <div className="mt-4 break-all rounded-sm bg-lightbg p-3 text-xs text-slate">
                  Manual key: {mfa.manual_key}
                </div>

                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  inputMode="numeric"
                  minLength={6}
                  maxLength={12}
                  placeholder="6-digit authentication code"
                  className="mt-5 w-full rounded-sm border border-brandborder px-3 py-2.5"
                />

                <button
                  type="submit"
                  disabled={busy}
                  className="mt-5 w-full rounded-sm bg-navy px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {busy ? "Verifying..." : "Complete MFA reset"}
                </button>
              </form>
            )
          )}
        </div>
      </div>
    </div>
  );
}
